// The visitor's clock, drawn as their sky.
//
// The point is a decision an agent makes constantly and currently makes blind:
// is this a reasonable moment to reach out? A timestamp in the agent's own
// zone cannot answer it, and a country name answers it even less — "India" and
// "Germany" are four and a half hours apart in a way no label conveys.
//
// So the sun's ACTUAL path for this location and date is drawn: sampled across
// their local day, with a disc at where the sun is right now, on the correct
// side of their horizon. Two of these side by side communicate the difference
// between two visitors immediately, which no pair of numbers does.
//
// It degrades honestly, and it degrades to something DELIBERATE. With no
// coordinates there is no real arc to draw — inventing a plausible sunrise for
// an unknown place would be fabrication — so the sky panel is dropped entirely
// for a compact sun/moon glyph beside the clock. An empty sky box with one dot
// in it looks like a bug, which is the one thing a "we don't know" state must
// not look like.

import { useEffect, useMemo, useState } from 'react';
import {
  PHASE_LABEL,
  offsetLabel,
  phaseFromElevation,
  phaseFromHour,
  resolveLocalTime,
  solarElevation,
  type SkyPhase,
} from '../lib/local-time';
import { displayTimeZone } from '../lib/datetime';
import styles from './LocalClock.module.css';

// Drawing box, in user units, at the proportions it is meant to be SEEN at.
// The scaling is uniform (no preserveAspectRatio override) — stretching this
// to the rail's width turned the sun into an ellipse and flattened the arc
// into something that read as a progress bar.
const W = 300;
const H = 76;
const HORIZON_Y = 56;
// The two halves of the sky get their own scale, and they must.
//
// Above the horizon the full 90° is mapped, so nothing clamps: Ahmedabad's
// noon sun reaches 73°, and a 60° ceiling flattened the top of the arc into a
// plateau — the curve stopped being an arc for the three hours that matter
// most. Below it, only the first 25° are mapped, because the rest of the night
// carries no information (−40° and −55° are both "dark") and a linear
// continuation ran the curve off the bottom of the box.
const MAX_ELEVATION = 90;
const MAX_DEPRESSION = 25;
const SAMPLES = 72;
/** Top of the drawable sky — leaves room for the disc at its highest. */
const SKY_TOP = 10;
/** Lowest the arc may dip, leaving the disc fully inside the box. */
const NIGHT_FLOOR = H - 8;

function elevationToY(elevation: number): number {
  if (elevation >= 0) {
    const up = Math.min(MAX_ELEVATION, elevation);
    return HORIZON_Y - (up / MAX_ELEVATION) * (HORIZON_Y - SKY_TOP);
  }
  const down = Math.min(MAX_DEPRESSION, -elevation);
  return HORIZON_Y + (down / MAX_DEPRESSION) * (NIGHT_FLOOR - HORIZON_Y);
}

/** Local hour → x. The box is one full local day, midnight to midnight. */
function hourToX(hour: number, minute = 0): number {
  return ((hour * 60 + minute) / (24 * 60)) * W;
}

/**
 * Keep the disc whole. Midnight sits at x=0 and midnight is exactly when the
 * arctic midsummer sun is at its lowest and most worth looking at — drawing it
 * half outside the frame is the one time of day this must not do that.
 */
function clampToBox(x: number, radius: number): number {
  return Math.max(radius + 1, Math.min(W - radius - 1, x));
}

export function LocalClock({
  timezone,
  latitude,
  longitude,
  place,
}: {
  timezone?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  place?: string | null;
}) {
  // A clock that does not move is a screenshot. Ticking once a minute is
  // enough — it is a wall clock, not a stopwatch — and costs one render.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const local = useMemo(
    () => resolveLocalTime({ timezone, longitude }, displayTimeZone(), now),
    [timezone, longitude, now],
  );

  const hasCoords =
    typeof latitude === 'number' &&
    typeof longitude === 'number' &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude);

  // The sun's path across the visitor's local day, sampled evenly. Computed in
  // UTC instants so the curve is the real one for that date, DST and all.
  const arc = useMemo(() => {
    if (!hasCoords || !local) return null;
    // Midnight local, expressed as a UTC instant.
    const localMidnight = new Date(
      now.getTime() - (local.hour * 60 + local.minute) * 60_000,
    );
    const points: string[] = [];
    for (let i = 0; i <= SAMPLES; i++) {
      const at = new Date(localMidnight.getTime() + (i / SAMPLES) * 86_400_000);
      const elevation = solarElevation(latitude, longitude, at);
      const x = (i / SAMPLES) * W;
      points.push(`${x.toFixed(1)},${elevationToY(elevation).toFixed(1)}`);
    }
    return points.join(' ');
  }, [hasCoords, latitude, longitude, local, now]);

  const elevationNow = useMemo(
    () => (hasCoords ? solarElevation(latitude, longitude, now) : null),
    [hasCoords, latitude, longitude, now],
  );

  if (!local) return null;

  const phase: SkyPhase =
    elevationNow === null ? phaseFromHour(local.hour) : phaseFromElevation(elevationNow);
  const isUp = elevationNow === null ? phase === 'day' || phase === 'golden' : elevationNow > 0;

  // Where "now" sits along the day, and how high the sun is.
  const sunRadius = isUp ? 5.5 : 4.5;
  const sunX = clampToBox(hourToX(local.hour, local.minute), sunRadius);
  const sunY = elevationNow === null ? SKY_TOP + 6 : elevationToY(elevationNow);

  const zoneNote =
    local.source === 'longitude'
      ? 'approximate — estimated from longitude'
      : (local.zone ?? '');

  return (
    <section className={styles.root} data-phase={phase} aria-label="Visitor local time">
      {arc ? (
        <svg className={styles.sky} viewBox={`0 0 ${W} ${H}`} role="img" aria-hidden>
          {/* Below-horizon band — the only fill, and a flat one. It is what
              makes a disc sitting under the line read as "night" at a glance. */}
          <rect className={styles.ground} x="0" y={HORIZON_Y} width={W} height={H - HORIZON_Y} />
          {/* Midnight / noon / midnight, so the arc has a day to sit inside
              rather than floating in an empty rectangle. */}
          {[6, 12, 18].map((h) => (
            <line
              key={h}
              className={styles.tick}
              x1={hourToX(h)}
              y1={SKY_TOP - 4}
              x2={hourToX(h)}
              y2={HORIZON_Y}
            />
          ))}
          <line className={styles.horizon} x1="0" y1={HORIZON_Y} x2={W} y2={HORIZON_Y} />
          <polyline className={styles.arc} points={arc} fill="none" />
          {/* A hairline down to the horizon anchors the disc to a time of day;
              without it the eye reads height but not "when". */}
          <line
            className={styles.nowLine}
            x1={sunX.toFixed(1)}
            y1={sunY.toFixed(1)}
            x2={sunX.toFixed(1)}
            y2={HORIZON_Y}
          />
          {/* Now. A filled disc when the sun is up, a ring when it is down —
              the same distinction the map's pins use for certain vs not. */}
          <circle
            className={isUp ? styles.sun : styles.moon}
            cx={sunX.toFixed(1)}
            cy={sunY.toFixed(1)}
            r={sunRadius}
          />
        </svg>
      ) : null}

      <div className={styles.readout}>
        {/* No coordinates, so no sky was drawn — the phase still gets a mark,
            inline, at a size that reads as an icon rather than as a gap. */}
        {!arc ? (
          <svg className={styles.glyph} viewBox="0 0 16 16" role="img" aria-hidden>
            <circle
              className={isUp ? styles.sun : styles.moon}
              cx="8"
              cy="8"
              r={isUp ? 4.5 : 5}
            />
            {isUp
              ? [0, 45, 90, 135, 180, 225, 270, 315].map((deg) => {
                  const r = (deg * Math.PI) / 180;
                  return (
                    <line
                      key={deg}
                      className={styles.ray}
                      x1={(8 + Math.cos(r) * 6).toFixed(2)}
                      y1={(8 + Math.sin(r) * 6).toFixed(2)}
                      x2={(8 + Math.cos(r) * 7.4).toFixed(2)}
                      y2={(8 + Math.sin(r) * 7.4).toFixed(2)}
                    />
                  );
                })
              : // A crescent, cut by a second disc rather than drawn as a path.
                <circle className={styles.moonBite} cx="10.6" cy="6.2" r="4.6" />}
          </svg>
        ) : null}
        <span className={styles.clock}>{local.clock}</span>
        <span className={styles.phase}>{PHASE_LABEL[phase]}</span>
        <span className={styles.offset}>{offsetLabel(local.offsetFromViewer)}</span>
        {zoneNote ? (
          <span className={styles.zone} title={place ?? undefined}>
            {zoneNote}
          </span>
        ) : null}
      </div>
    </section>
  );
}
